import React, { useState, useEffect, useMemo } from "react";
import { Post } from "@/core/types/postTypes";
import { postsService } from "./services/postsService";
import { ScheduleCalendar } from "@/components/ui/ScheduleCalendar";
import { BestTimesChips } from "@/components/ui/BestTimesChips";
import {
  useAudienceActivity,
  getBestTimesForDay,
  getDailyActivity,
} from "@/hooks/useAudienceActivity";
import { getPlatformKeys, PLATFORM_REGISTRY } from "@/config/platforms";
import { apiClient } from "@/services/apiClient";
import { WarningIcon } from "@/components/icons";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";

interface SchedulePostModalProps {
  post: Post;
  clientId: string;
  onClose: () => void;
  onScheduled?: () => void;
}

function getDayName(date: Date): string {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return days[date.getDay()];
}

function dateToISOString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const SchedulePostModal: React.FC<SchedulePostModalProps> = ({
  post,
  clientId,
  onClose,
  onScheduled,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [timezone, setTimezone] = useState("Europe/Rome");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasConnectedPlatform, setHasConnectedPlatform] = useState<boolean | null>(null);

  const { data: activityData, isLoading: isLoadingActivity } = useAudienceActivity(clientId);

  // Check if any social media account is connected
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await apiClient.get(
          `/api/social-media/status/${clientId}`
        );
        const status = response.data;
        const connected = Object.keys(PLATFORM_REGISTRY).some((key) => {
          return status?.[key]?.connected === true;
        });
        setHasConnectedPlatform(connected);
      } catch {
        // If we can't check, don't block scheduling
        setHasConnectedPlatform(null);
      }
    };
    checkConnection();
  }, [clientId]);

  // Initialize date/time from existing schedule or default to tomorrow
  useEffect(() => {
    if (post.scheduledAt) {
      const scheduled = new Date(post.scheduledAt);
      const dateOnly = new Date(scheduled);
      dateOnly.setHours(0, 0, 0, 0);
      setSelectedDate(dateOnly);
      const hours = scheduled.getHours().toString().padStart(2, "0");
      const minutes = scheduled.getMinutes().toString().padStart(2, "0");
      setSelectedTime(`${hours}:${minutes}`);
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      setSelectedDate(tomorrow);
    }
  }, [post.scheduledAt]);

  // Get activity data for the calendar from first available platform
  const dailyActivity = useMemo(() => {
    if (!activityData) return {};
    for (const platform of getPlatformKeys()) {
      const activity = getDailyActivity(activityData, platform);
      if (activity && Object.keys(activity).length > 0) return activity;
    }
    return {};
  }, [activityData]);

  // Get best times for selected day from first available platform
  const bestTimes = useMemo(() => {
    if (!selectedDate || !activityData) return [];
    const dayName = getDayName(selectedDate).toLowerCase();
    for (const platform of getPlatformKeys()) {
      const times = getBestTimesForDay(activityData, platform, dayName);
      if (times.length > 0) return times;
    }
    return [];
  }, [selectedDate, activityData]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
  };

  const handleTimeChipSelect = (hour: number) => {
    const timeStr = `${hour.toString().padStart(2, "0")}:00`;
    setSelectedTime(timeStr);
  };

  const handleSchedule = async () => {
    if (!selectedDate) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const dateStr = dateToISOString(selectedDate);
      const localDateTimeStr = `${dateStr}T${selectedTime}`;

      const result = await postsService.schedulePost(
        clientId,
        post.id,
        localDateTimeStr,
        timezone
      );

      if (result.success) {
        setSuccess(true);
        onScheduled?.();
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Failed to schedule post";
      setError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Schedule Post"
      size="lg"
      footer={
        !success ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSchedule}
              disabled={isSubmitting || !selectedDate || !selectedTime}
            >
              {isSubmitting ? "Scheduling..." : "Schedule Post"}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="p-6">
        {success ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">
              Post scheduled successfully!
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-4">
              Schedule "{post.groupName || post.id}" for publication
            </p>

            {hasConnectedPlatform === false && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <WarningIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">No social media accounts connected</p>
                    <p className="text-xs text-amber-700 mt-1">
                      You can still schedule this post, but publishing will fail unless an account is
                      connected before the scheduled time.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Calendar Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date
                </label>
                {isLoadingActivity ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <ScheduleCalendar
                    selectedDate={selectedDate}
                    onSelectDate={handleDateSelect}
                    activityByDay={dailyActivity}
                  />
                )}
              </div>

              {/* Best Times Chips */}
              {selectedDate && bestTimes.length > 0 && (
                <BestTimesChips
                  times={bestTimes}
                  onSelectTime={handleTimeChipSelect}
                  selectedTime={selectedTime}
                  dayName={getDayName(selectedDate)}
                />
              )}

              {/* Manual Time Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  step={300}
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Europe/Rome">Europe/Rome (CET)</option>
                  <option value="America/New_York">America/New_York (EST)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                  <option value="Europe/London">Europe/London (GMT)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                </select>
              </div>

              {/* Preview */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Scheduled:</strong>{" "}
                  {selectedDate && selectedTime
                    ? new Date(
                        `${dateToISOString(selectedDate)}T${selectedTime}`
                      ).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : "Select date and time"}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
